/**
 * Inspection Component Resolver
 *
 * Resolves which components a check is about:
 * - Template has componentBaseTypeGroup (Skickbedömning) → every live component that resolves
 *   to that assessment group (Helpers/assessmentGroups.ts)
 * - Check has componentKey → that component, while it is live
 * - Neither → empty array (a building-wide check)
 */

import { UtilityCategory } from '../../types/Enums';
import { getDatabase, ref, get } from 'firebase/database';
import { BaseComponentImpl } from '../../types/implementations';
import { BaseComponent, Building } from '../../types/protocols';
import { BuildComponentTypeDefinition } from '../../types/Enums';
import { AssessmentGroupKey, isLiveComponent, resolveAssessmentGroup } from '../../Helpers/assessmentGroups';

export interface ResolvedComponent {
    key: string;
    displayName: string;
    utilityCategory?: UtilityCategory;
}

/**
 * Resolve the components of a check.
 *
 * @param buildingKey - The building's Firebase key
 * @param building - The building object (needed for displayName resolution)
 * @param componentKey - The check's component (if set)
 * @param baseTypeGroup - The template's assessment group (if set; wins over componentKey)
 * @param typeDefinitions - Component type definitions, needed to place doors by door category
 * @returns Array of resolved components sorted by displayName
 */
export async function resolveInspectionComponents(
    buildingKey: string,
    building: Building | null,
    componentKey: string | undefined,
    baseTypeGroup?: AssessmentGroupKey,
    typeDefinitions: Record<string, BuildComponentTypeDefinition> = {}
): Promise<ResolvedComponent[]> {
    const db = getDatabase();
    const componentsRef = ref(db, `buildings/${buildingKey}/structure/components`);

    try {
        const snapshot = await get(componentsRef);
        if (!snapshot.exists()) return [];

        const components: ResolvedComponent[] = [];
        const data = snapshot.val() as Record<string, BaseComponent>;

        const toResolved = (key: string, comp: BaseComponent): ResolvedComponent => {
            const { key: _existingKey, ...compWithoutKey } = comp as BaseComponent & { key?: string };
            const impl = new BaseComponentImpl({ ...compWithoutKey, key });
            return {
                key,
                displayName: building ? impl.displayName(building) : (comp.label?.value || key),
                utilityCategory: comp.utilityCategory
            };
        };

        if (baseTypeGroup) {
            for (const [key, comp] of Object.entries(data)) {
                if (isLiveComponent(comp) && resolveAssessmentGroup(comp, data, typeDefinitions) === baseTypeGroup) {
                    components.push(toResolved(key, comp));
                }
            }
        } else if (componentKey && isLiveComponent(data[componentKey])) {
            components.push(toResolved(componentKey, data[componentKey]));
        }

        return components.sort((a, b) => a.displayName.localeCompare(b.displayName, 'sv'));
    } catch (error) {
        console.error('Failed to resolve inspection components:', error);
        throw error;  // Let caller handle
    }
}
